// Runway wind components for the crosswind calculator. Ported from
// webplan-modern's windComponents(), signedCrosswind() and
// runwayDesignator(). Directions in degrees (the runway's heading and where
// the wind blows FROM, both magnetic as the tower gives them), speeds in
// knots. Pure; see navigation.ts.

import { normaliseDegrees } from './navigation'

function round1(n: number) {
  return Math.round(n * 10) / 10
}

export interface RunwayWind {
  /** Along the runway: positive is a headwind, negative a tailwind. */
  headwindKt: number
  /** Across it: positive from the right, negative from the left. */
  crosswindKt: number
}

export function runwayWind(runwayHeading: number, windDir: number, windSpeedKt: number): RunwayWind {
  const angle = ((windDir - runwayHeading) * Math.PI) / 180
  return {
    headwindKt: round1(windSpeedKt * Math.cos(angle)) + 0,
    crosswindKt: round1(windSpeedKt * Math.sin(angle)) + 0,
  }
}

/** "04", "22", "36" for a heading in degrees; 0 and 360 are both runway 36. */
export function runwayDesignator(heading: number): string {
  let designator = Math.round(normaliseDegrees(heading) / 10)
  if (designator === 0) designator = 36
  return String(designator).padStart(2, '0')
}

export interface RunwayChoice {
  /** The runway end facing into the wind (the given one on a dead crosswind). */
  heading: number
  designator: string
  wind: RunwayWind
  /** null when the aircraft has no demonstrated crosswind figure. */
  overLimit: boolean | null
}

/** The better end of a runway for this wind, and whether its crosswind is over the aircraft's figure. */
export function favouredRunway(
  runwayHeading: number,
  windDir: number,
  windSpeedKt: number,
  crosswindLimitKt: number | null,
): RunwayChoice {
  let heading = normaliseDegrees(runwayHeading)
  let wind = runwayWind(heading, windDir, windSpeedKt)
  if (wind.headwindKt < 0) {
    heading = normaliseDegrees(heading + 180)
    wind = runwayWind(heading, windDir, windSpeedKt)
  }
  return {
    heading,
    designator: runwayDesignator(heading),
    wind,
    overLimit: crosswindLimitKt === null ? null : Math.abs(wind.crosswindKt) > crosswindLimitKt,
  }
}
